import type Phaser from "phaser";
import { CANVAS_COLOURS } from "daedalus-core";

/**
 * Reference links on the map (ADR-070's amendment "typed references"): a line from each referring thing to its
 * target with an arrowhead at the target, in `overlay-link`, at a fixed screen width at any zoom. One
 * `Graphics` object over the map, redrawn only when the links it shows or the zoom change.
 */

/**
 * The link's colour, from the canvas colour table.
 */
export const LINK_COLOUR = CANVAS_COLOURS.link.rgb;

/**
 * The line's width in screen pixels.
 */
export const LINK_LINE = 1.5;

/**
 * The arrowhead's length and half its width, in screen pixels.
 */
export const LINK_HEAD = { length: 8, halfWidth: 4 } as const;

/**
 * One link to draw, in world pixels.
 */
export interface LinkSegment {
  readonly from: { readonly x: number; readonly y: number };
  readonly to: { readonly x: number; readonly y: number };
}

/**
 * The arrowhead's three corners for a link: its tip at the target and its base `LINK_HEAD.length` screen
 * pixels back along the line, or null for a link of no length.
 *
 * @param segment The link.
 * @param zoom The camera's zoom, which turns screen pixels into world pixels.
 * @returns The tip and the base's two corners, in world pixels, or null.
 */
export function arrowHead(segment: LinkSegment, zoom: number): readonly { readonly x: number; readonly y: number }[] | null {
  const dx = segment.to.x - segment.from.x;
  const dy = segment.to.y - segment.from.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return null;
  }
  const ux = dx / length;
  const uy = dy / length;
  const back = Math.min(LINK_HEAD.length / zoom, length);
  const half = LINK_HEAD.halfWidth / zoom;
  const bx = segment.to.x - ux * back;
  const by = segment.to.y - uy * back;
  return [
    { x: segment.to.x, y: segment.to.y },
    { x: bx - uy * half, y: by + ux * half },
    { x: bx + uy * half, y: by - ux * half },
  ];
}

/**
 * The links of one scene.
 */
export class LinkView {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private last = "";

  /**
   * @param scene Scene to draw into.
   * @param depth Depth over the map's layers.
   */
  public constructor(scene: Phaser.Scene, depth: number) {
    this.graphics = scene.add.graphics().setDepth(depth);
  }

  /**
   * Draws the links, or clears them, when they or the zoom changed since the last call.
   *
   * @param segments The links to draw.
   * @param zoom The camera's zoom.
   * @returns True when a redraw happened.
   */
  public sync(segments: readonly LinkSegment[], zoom: number): boolean {
    const signature = `${zoom}|${segments.map((s) => `${s.from.x},${s.from.y},${s.to.x},${s.to.y}`).join(";")}`;
    if (signature === this.last) {
      return false;
    }
    this.last = signature;
    const g = this.graphics;
    g.clear();
    g.lineStyle(LINK_LINE / zoom, LINK_COLOUR, CANVAS_COLOURS.link.alpha);
    g.fillStyle(LINK_COLOUR, CANVAS_COLOURS.link.alpha);
    for (const segment of segments) {
      const head = arrowHead(segment, zoom);
      if (head === null) {
        continue;
      }
      const [tip, left, right] = head as [{ x: number; y: number }, { x: number; y: number }, { x: number; y: number }];
      const baseX = (left.x + right.x) / 2;
      const baseY = (left.y + right.y) / 2;
      g.lineBetween(segment.from.x, segment.from.y, baseX, baseY);
      g.fillTriangle(tip.x, tip.y, left.x, left.y, right.x, right.y);
    }
    return true;
  }

  /**
   * Removes the drawing.
   */
  public destroy(): void {
    this.graphics.destroy();
  }
}
